import React, { useState, useEffect } from 'react';
import './Admin.css';

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState(''); // Store the actual password
  const [tours, setTours] = useState({});
  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    category: 'popular',
    name: '',
    days: '',
    price: '',
    image: '',
    description: '',
    features: [],
    amenities: ['Flight', 'Trains', 'Volvo', 'Hotels', 'Sightseeing', 'Meals', 'Transfers'], // default all selected
      discount: 0,
  });
  // Amenities for icon selection
  const availableAmenities = [
    'Flight',
    'Trains',
    'Volvo',
    'Hotels',
    'Sightseeing',
    'Meals',
    'Transfers'
  ];
  const handleAmenityChange = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Available features for selection
  const availableFeatures = [
    'Free WiFi',
    'Swimming Pool',
    'Spa & Wellness',
    'Fitness Center',
    'Restaurant',
    'Room Service',
    'Airport Transfer',
    'Tour Guide',
    'Breakfast Included',
    'Air Conditioning',
    'Mini Bar',
    'Balcony',
    'City View',
    'Sea View',
    'Mountain View',
    'Pet Friendly',
    'Business Center',
    'Parking',
    'Concierge Service',
    'Laundry Service'
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    if (password) {
      setAdminPassword(password); // Store password for API calls
      setIsLoggedIn(true);
      setPassword('');
    }
  };

  // Fetch tours when admin password is set
  useEffect(() => {
    if (adminPassword && isLoggedIn) {
      fetchTours();
    }
  }, [adminPassword, isLoggedIn]);

  const fetchTours = async () => {
    try {
      const response = await fetch('https://travel-agency-backend-travel.onrender.com/admin/tours', {
        headers: { 'X-Admin-Password': adminPassword }
      });
      
      if (!response.ok) {
        setMessage('Error: Unauthorized or failed to fetch tours');
        return;
      }
      
      const data = await response.json();
      setTours(data);
      setCategories(Object.keys(data));
    } catch (error) {
      console.error('Error fetching tours:', error);
      setMessage('Error fetching tours: ' + error.message);
    }
  };

  const handleAddTour = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://travel-agency-backend-travel.onrender.com/admin/tour/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok) {
        setMessage('Tour added successfully!');
        setFormData({
          category: 'popular',
          name: '',
          days: '',
          price: '',
          image: '',
          description: '',
          features: [],
          amenities: ['Flight', 'Trains', 'Volvo', 'Hotels', 'Sightseeing', 'Meals', 'Transfers'],
            discount: 0,
        });
        setShowAddForm(false);
        fetchTours();
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error adding tour: ' + error.message);
    }
  };

  const handleUpdateTour = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`https://travel-agency-backend-travel.onrender.com/admin/tour/${editingTour.id}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok) {
        setMessage('Tour updated successfully!');
        setEditingTour(null);
        setFormData({
          category: 'popular',
          name: '',
          days: '',
          price: '',
          image: '',
          description: '',
          features: [],
          amenities: ['Flight', 'Trains', 'Volvo', 'Hotels', 'Sightseeing', 'Meals', 'Transfers']
        });
        fetchTours();
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error updating tour: ' + error.message);
    }
  };

  const handleDeleteTour = async (tourId) => {
    if (window.confirm('Are you sure you want to delete this tour?')) {
      try {
        const response = await fetch(`https://travel-agency-backend-travel.onrender.com/admin/tour/${tourId}/delete`, {
          method: 'DELETE',
          headers: { 'X-Admin-Password': adminPassword }
        });

        const data = await response.json();
        if (response.ok) {
          setMessage('Tour deleted successfully!');
          fetchTours();
        } else {
          setMessage('Error: ' + data.error);
        }
      } catch (error) {
        setMessage('Error deleting tour: ' + error.message);
      }
    }
  };

  const handleEditTour = (tour, category) => {
    setEditingTour(tour);
    setShowAddForm(false); // Close add form if open
    setFormData({
      category: category,
      name: tour.name,
      days: tour.days,
      price: tour.price,
      image: tour.image,
      description: tour.description,
      features: tour.features || [],
      amenities: tour.amenities || ['Flight', 'Trains', 'Volvo', 'Hotels', 'Sightseeing', 'Meals', 'Transfers'],
        discount: tour.discount || 0,
    });
    // Scroll to top to see the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Admin Login</h1>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Admin Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
              />
            </div>
            <button type="submit" className="login-btn">Login</button>
          </form>
          {/* <p className="hint">Default password: admin123</p> */}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>🛠️ Admin Panel</h1>
        <button className="logout-btn" onClick={() => setIsLoggedIn(false)}>Logout</button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="admin-content">
        <button className="add-btn" onClick={() => {
          setShowAddForm(!showAddForm);
          setEditingTour(null);
          setFormData({
            category: 'popular',
            name: '',
            days: '',
            price: '',
            image: '',
            description: '',
            features: [],
            amenities: ['Flight', 'Trains', 'Volvo', 'Hotels', 'Sightseeing', 'Meals', 'Transfers']
          });
        }}>
          {showAddForm ? '✕ Cancel' : '+ Add New Tour'}
        </button>

        {(showAddForm || editingTour) && (
          <div className="form-container" style={{ position: 'relative' }}>
            <button
              type="button"
              className="form-close-btn"
              style={{ position: 'absolute', top: 17, right: 43, fontSize: '3.5rem', background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', zIndex: 2 }}
              onClick={() => { setShowAddForm(false); setEditingTour(null); }}
              aria-label="Close form"
            >
              ×
            </button>
            <h2>{editingTour ? 'Edit Tour' : 'Add New Tour'}</h2>
            <form onSubmit={editingTour ? handleUpdateTour : handleAddTour}>
              <div className="form-group">
                <label>Category:</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  disabled={editingTour}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'uk' ? 'WorldWide Tours' :
                       cat === 'eurostar' ? 'Honneymoon Packages' :
                       cat === 'faraway' ? 'Family Packages' :
                       cat.charAt(0).toUpperCase() + cat.slice(1) + ' Tours'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tour Name:</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Days:</label>
                  <input
                    type="number"
                    name="days"
                    value={formData.days}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price (Rs.):</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Image URL:</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleFormChange}
                  placeholder="https://..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="3"
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Amenities (Select icons to show):</label>
                <div className="features-grid">
                  <div className="form-group">
                    <label>Discount:</label>
                    <select
                      name="discount"
                      value={formData.discount}
                      onChange={handleFormChange}
                      required
                    >
                      <option value={0}>No Discount</option>
                      <option value={10}>10% OFF</option>
                      <option value={20}>20% OFF</option>
                      <option value={30}>30% OFF</option>
                      <option value={40}>40% OFF</option>
                      <option value={50}>50% OFF</option>
                      <option value={60}>60% OFF</option>
                      <option value={70}>70% OFF</option>
                      <option value={80}>80% OFF</option>
                    </select>
                  </div>
                  {availableAmenities.map(amenity => (
                    <label key={amenity} className="feature-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.amenities.includes(amenity)}
                        onChange={() => handleAmenityChange(amenity)}
                      />
                      <span>{amenity}</span>
                    </label>
                  ))}
                </div>
                <div className="selected-features">
                  <strong>Selected amenities ({formData.amenities.length}):</strong>
                  {formData.amenities.length > 0 ? (
                    <div className="feature-tags">
                      {formData.amenities.map(amenity => (
                        <span key={amenity} className="feature-tag">
                          {amenity}
                          <button type="button" onClick={() => handleAmenityChange(amenity)}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="no-features">No amenities selected</span>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Features (Select multiple):</label>
                <div className="features-grid">
                  {availableFeatures.map(feature => (
                    <label key={feature} className="feature-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.features.includes(feature)}
                        onChange={() => handleFeatureChange(feature)}
                      />
                      <span>{feature}</span>
                    </label>
                  ))}
                </div>
                <div className="selected-features">
                  <strong>Selected features ({formData.features.length}):</strong>
                  {formData.features.length > 0 ? (
                    <div className="feature-tags">
                      {formData.features.map(feature => (
                        <span key={feature} className="feature-tag">
                          {feature}
                          <button type="button" onClick={() => handleFeatureChange(feature)}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="no-features">No features selected</span>
                  )}
                </div>
              </div>

              <button type="submit" className="submit-btn">
                {editingTour ? 'Update Tour' : 'Add Tour'}
              </button>
            </form>
          </div>
        )}

        <div className="tours-list">
          {categories.length > 0 ? (
            categories.map(category => (
              <div key={category} className="category-section">
                <h2>{
                  category === 'uk' ? 'WorldWide Tours' :
                  category === 'eurostar' ? 'Honneymoon Packages' :
                  category === 'faraway' ? 'Family Packages' :
                  category.charAt(0).toUpperCase() + category.slice(1) + ' Tours'
                } ({tours[category]?.length || 0})</h2>
                <div className="tours-table">
                  {tours[category] && Array.isArray(tours[category]) && tours[category].length > 0 ? (
                    tours[category].map(tour => (
                      <div key={tour.id} className="tour-row">
                        <div className="tour-info">
                          <h3>{tour.name}</h3>
                          <p>{tour.days} days | Rs.{tour.price}</p>
                          <p className="desc">{tour.description}</p>
                        </div>
                        <div className="tour-actions">
                          <button
                            className="edit-btn"
                            onClick={() => handleEditTour(tour, category)}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteTour(tour.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-tours">No tours in this category</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p>Loading tours...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
